import { FC, useMemo, useState } from 'react'

import { Dropdown, HStack, Text } from '@revolut-internal/ui-kit'

import { ActionDropdown } from 'components/ActionDropdown/ActionDropdown'
import { NestedDropdownItemBase } from 'components/Filters/NestedDropdownItemBase'
import {
  RangeFilterContent,
  RangeFilterValue,
} from 'components/Filters/RangeFilter/RangeFilterContent'
import { MessageAuthor, ValueOf } from 'types'
import { convertTimeStringToDate } from 'utils/time/convertTimeStringToDate'
import { getTimeStringFromDate } from 'utils/time/getTimeStringFromDate'

import { useAdditionalFilters } from '../AdditionalFiltersContext'
import { LAST_MESSAGE_AUTHOR_KEY, LAST_MESSAGE_DATE_KEY } from '../consts'
import { checkIfDateRangeValue } from '../utils/checkIfDateRangeValue'
import { createLastHoursPreset, createLastMinutesPreset } from '../utils/datePresets'

type LastMessageSentFilterProps = {
  open: boolean
  onClose: VoidFunction
}

const CALENDAR_FILTER_MAX_HEIGHT = 600
const FILTER_PRESETS = [
  createLastMinutesPreset(10),
  createLastMinutesPreset(30),
  createLastHoursPreset(1),
]
const ExtendedMessageAuthor = {
  ...MessageAuthor,
  Anyone: 'ANYONE',
}
type ExtendedMessageAuthor = ValueOf<typeof ExtendedMessageAuthor>

const FILTER_OPTIONS = [
  {
    key: ExtendedMessageAuthor.Agent,
    value: ExtendedMessageAuthor.Agent,
    label: 'Agent',
  },
  {
    key: ExtendedMessageAuthor.Customer,
    value: ExtendedMessageAuthor.Customer,
    label: 'Customer',
  },
  {
    key: ExtendedMessageAuthor.Anyone,
    value: ExtendedMessageAuthor.Anyone,
    label: 'Anyone',
  },
]

const normaliseAuthorValue = (value: ExtendedMessageAuthor): MessageAuthor[] => {
  if (value === ExtendedMessageAuthor.Anyone) {
    return [MessageAuthor.Agent, MessageAuthor.Customer]
  }

  return [value] as MessageAuthor[]
}

export const LastMessageSentFilter: FC<LastMessageSentFilterProps> = ({
  open,
  onClose,
}) => {
  const { additionalFilters, setAdditionalFilters, removeAdditionalFilters } =
    useAdditionalFilters()

  const rawLastMessageDateValue = additionalFilters[LAST_MESSAGE_DATE_KEY]

  const dateRangeValue = checkIfDateRangeValue(rawLastMessageDateValue)
    ? rawLastMessageDateValue
    : null

  const [activeAuthorTypes, setActiveAuthorTypes] = useState<ExtendedMessageAuthor>(
    () => {
      const rawLastMessageAuthorValue = additionalFilters[LAST_MESSAGE_AUTHOR_KEY]

      if (
        !rawLastMessageAuthorValue ||
        !Array.isArray(rawLastMessageAuthorValue) ||
        rawLastMessageAuthorValue.length !== 1
      ) {
        return ExtendedMessageAuthor.Anyone
      }

      return rawLastMessageAuthorValue[0]
    },
  )

  const dateRange = useMemo(() => {
    if (!dateRangeValue) {
      return undefined
    }

    return {
      timeRange: {
        from: dateRangeValue.from,
        to: dateRangeValue.to,
      },
      start: getTimeStringFromDate(dateRangeValue.from),
      end: getTimeStringFromDate(dateRangeValue.to),
    }
  }, [dateRangeValue])

  const handleCustomDateRangeChange = (value: RangeFilterValue | null) => {
    if (!value?.timeRange) {
      removeAdditionalFilters(['lastMessageDate', 'lastMessageAuthor'])
      onClose()
      return
    }

    const { timeRange, start, end } = value

    const fromTimestamp =
      convertTimeStringToDate({
        timeStr: start,
        baseDate: new Date(timeRange.from),
        shouldReturnDate: true,
      })?.getTime() ?? timeRange.from

    const toTimestamp =
      convertTimeStringToDate({
        timeStr: end,
        baseDate: new Date(timeRange.to),
        shouldReturnDate: true,
      })?.getTime() ?? timeRange.to

    setAdditionalFilters({
      [LAST_MESSAGE_DATE_KEY]: {
        from: fromTimestamp,
        to: toTimestamp,
      },
      [LAST_MESSAGE_AUTHOR_KEY]: normaliseAuthorValue(activeAuthorTypes),
    })
    onClose()
  }

  return (
    <NestedDropdownItemBase
      label="Last message sent"
      renderValue={() => (dateRangeValue ? '1' : '')}
    >
      {(isShown, anchorRef) =>
        isShown && (
          <Dropdown
            open={open}
            anchorRef={anchorRef}
            maxHeight={CALENDAR_FILTER_MAX_HEIGHT}
            onClick={e => e.stopPropagation()}
          >
            <RangeFilterContent
              labelEmptyValue="Select date"
              timeRange={dateRange}
              presets={FILTER_PRESETS}
              disabledDays={{ after: new Date() }}
              supportsTime
              onSave={handleCustomDateRangeChange}
            >
              <HStack px="s-16" pb="s-16" space="s-8" align="center">
                <Text variant="emphasis2">Sent by:</Text>

                <ActionDropdown
                  label="Select author"
                  options={FILTER_OPTIONS}
                  value={activeAuthorTypes}
                  onSelect={setActiveAuthorTypes}
                />
              </HStack>
            </RangeFilterContent>
          </Dropdown>
        )
      }
    </NestedDropdownItemBase>
  )
}
